import { Send as SendIcon } from "@mui/icons-material";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import AsyncSelectScroll from "@/components/AsyncSelectScroll";
import Text from "../../../../../../components/Text";
import StyledButtonV2 from "../../../../../../components/app/StyledButtonV2";
import StyledFormModal from "../../../../../../components/globalModal/StyledModal";
import RichTextEditor from "../../../../../../components/richTextEditor/RichTextEditor";
import { useDebounce } from "../../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../hooks/useSnackbar";
import { useSendSmsToPatient } from "../../../../../../queries/patient/use-patient-send-sms-to-patient";
import { useRosTemplateById } from "../../../../../../queries/template/new-flow/use-new-template-by-id-list";
import { useNewTemplateList } from "../../../../../../queries/template/new-flow/use-new-template-list";
import { GetAllClaimTemplateTypesEnum } from "../../../../../../services";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
  showToastOnApiCall,
} from "../../../../../../utils/utils";

interface SMSModalProps {
  disclosureHost: DisclosureHost;
  patientName?: string;
  patientMRN?: string;
  patientUuid?: string;
  onSave?: () => void;
}

const SMSModal = ({
  disclosureHost,
  patientName,
  patientMRN,
  patientUuid,
  onSave,
}: SMSModalProps) => {
  const { mutateAsync: sendMessageToPatient, isPending } =
    useSendSmsToPatient();
  const showSnackbar = useSnackbar();

  const [selectedTemplate, setSelectedTemplate] = useState<any>();
  const [searchTemplate, setSearchTemplate] = useDebounce<string>("");
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);

  const {
    data: templateList,
    isLoading: isTemplateListLoading,
    isRefetching: isTemplateListRefetching,
    isFetching: isTemplateListFetching,
  } = useNewTemplateList({
    pagination,
    searchKey: searchTemplate,
    templateType: GetAllClaimTemplateTypesEnum.Sms,
    xTENANTID: getXtenantId(),
  });

  const { data: selectedTemplateData } = useRosTemplateById({
    rosTemplateUuid: selectedTemplate?.value,
    xTENANTID: getXtenantId(),
  });

  const initialValue = {
    message: "",
  };

  const validationSchema = Yup.object({
    message: Yup.string().trim().required("Message is required"),
    // .max(160, "Message must be 160 characters or less"),
  });

  const formik = useFormik({
    initialValues: initialValue,
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  useEffect(() => {
    if (selectedTemplateData?.data && selectedTemplate?.value) {
      formik.setFieldValue(
        "message",
        selectedTemplateData?.data?.description ?? ""
      );
    }
  }, [selectedTemplateData?.data, selectedTemplate?.value]);

  async function handleSubmit(values: typeof initialValue) {
    if (!patientUuid) {
      return;
    }
    const payload = {
      emailToPatient: {
        description: values.message,
        patientUuids: [patientUuid],
      },
      xTENANTID: getXtenantId(),
    };

    showToastOnApiCall({
      apiCall: sendMessageToPatient(payload),
      showSnackbar,
      onSave: () => {
        handleClose();
        onSave?.();
      },
    });
  }

  function handleClose() {
    setSelectedTemplate(undefined);
    formik.resetForm();
    disclosureHost.close();
  }

  // const characterCount = formik.values.message.length;

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={handleClose}
      dialogTitle="Send Text Message"
      dialogAction={
        <>
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={handleClose}
          />
          <StyledButtonV2
            label="Send Message"
            variant="contained"
            onClick={formik.submitForm}
            sIcon={<SendIcon sx={{ fontSize: "14px" }} />}
            disabled={isPending}
            sx={{ ml: 1.2 }}
          />
        </>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "32vw",
        },
      }}
    >
      <Box display="flex" flexDirection="column" gap={2} width="100%" pt={1}>
        {/* Patient Information Section */}
        <Box
          sx={{
            padding: "8px 0px",
            borderRadius: "4px",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 5,
            width: "100%",
          }}
        >
          <Text fontWeight="medium">
            <span style={{ color: "#999" }}>Patient:</span>{" "}
            <strong>{patientName}</strong>
          </Text>
          <Text fontWeight="medium">
            <span style={{ color: "#999" }}>MRN:</span>{" "}
            <strong>{patientMRN}</strong>
          </Text>
        </Box>

        {/* Template Selection */}
        <Box width="100%">
          <AsyncSelectScroll
            loading={
              isTemplateListLoading ||
              isTemplateListRefetching ||
              isTemplateListFetching
            }
            placeholder="Select Template"
            onSearch={(v) => {
              setSearchTemplate(v);
              setPagination((p) => ({ ...p, page: 0 }));
            }}
            options={templateList?.data?.content?.map((it) => ({
              value: it?.uuid,
              label: it?.templateName,
            }))}
            value={selectedTemplate}
            onChange={(val) => setSelectedTemplate(val)}
            pagination={pagination}
            setPagination={setPagination}
            totalElements={templateList?.data?.page?.totalElements}
          />
        </Box>

        {/* Message Input Section */}
        <Box width="100%" flex={1} display="flex" flexDirection="column">
          <Text fontSize="12px" sx={{ mb: 0.5, color: "#666" }}>
            Message<span style={{ color: "#d32f2f" }}>*</span>
          </Text>
          <RichTextEditor
            name="message"
            value={formik.values.message}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter your message..."
            minRows={10}
            error={formik.touched.message && (formik.errors.message as any)}
          />
          {/* <Text fontSize="12px" sx={{ mt: 0.5, color: "#666" }}>
            Character count: {characterCount} / 160
          </Text> */}
        </Box>
      </Box>
    </StyledFormModal>
  );
};

export default SMSModal;
